import { AdPlatformType } from '@app/core/models/common.models';
import {
  AdspertPlanType,
  BillingVolumeType,
  CustomerSubscription,
  SubscriptionInvoicePeriod,
  SubscriptionStatus,
} from '@app/core/models/customer.models';
import { microsToNumber } from '@app/core/utilities/number.utilities';

export function isLegacySubscription(subscription: CustomerSubscription): boolean {
  return !!subscription?.legacy;
}

export function isPrimordialSubscription(subscription: CustomerSubscription): boolean {
  return [AdspertPlanType.Standard, AdspertPlanType.Lite].includes(subscription?.type);
}

export function isManagedSubscription(subscription: CustomerSubscription): boolean {
  return subscription?.type === AdspertPlanType.Managed;
}

export function hasActiveSubscription(subscription: CustomerSubscription): boolean {
  return subscription?.status === SubscriptionStatus.Active;
}

export function hasEndingSubscription(subscription: CustomerSubscription): boolean {
  return subscription?.status === SubscriptionStatus.Ending;
}

export function hasTerminatedSubscription(subscription: CustomerSubscription): boolean {
  return subscription?.status === SubscriptionStatus.Terminated;
}

export function getAdSpendPercentage(subscription: CustomerSubscription): number {
  return (
    subscription?.pricingDetails?.volumePercentage?.find((fee) => fee.volumeType === BillingVolumeType.AdSpend)
      ?.tiers?.[0]?.percentage || 0
  );
}

export function parseSubscriptionResponse<T extends { data?: CustomerSubscription | null }>(response: T): T {
  sortPlatformGroups(response.data.pricingDetails?.volumeFlatFeeOrPercentage);
  sortPlatformGroups(response.data.billingVolume);
  response.data.nextInvoiceAmount =
    response.data.nextInvoiceAmount && microsToNumber(response.data.nextInvoiceAmount, 0);

  response.data.pricingDetails?.volumePercentage?.forEach((volumePercentage) => {
    volumePercentage.tiers.sort((a, b) => {
      const aThreshold = a.threshold ? a.threshold : Number.POSITIVE_INFINITY;
      const bThreshold = b.threshold ? b.threshold : Number.POSITIVE_INFINITY;

      return aThreshold - bThreshold;
    });

    volumePercentage.tiers.forEach((tier) => {
      tier.percentage = parseFloat(tier.percentage.toString()) / 100;
    });
  });

  response.data.renewalDiscounts?.forEach((discount) => {
    discount.percentOff = parseFloat(discount.percentOff.toString()) / 100;
  });

  return response;
}

export function sortPlatformGroups<T extends { platforms: AdPlatformType[] }[]>(groups: T | undefined): void {
  groups?.forEach(({ platforms }) => platforms.sort());

  groups?.sort((a, b) => {
    if (a.platforms.length <= 1 && b.platforms.length <= 1) {
      const platformA = a.platforms[0];
      const platformB = b.platforms[0];

      if (platformA === 'AMAZON') {
        return -2;
      }
      if (platformB === 'AMAZON') {
        return 2;
      }
      if (platformA === 'EBAY') {
        return -1;
      }
      if (platformB === 'EBAY') {
        return 1;
      }
      return 0;
    }

    const mp = a.platforms.length <= 1 ? -1 : b.platforms.length <= 1 ? 1 : 0;

    if (mp) {
      const platform = mp === -1 ? a.platforms[0] : b.platforms[0];

      return (platform === 'AMAZON' ? 2 : platform === 'EBAY' ? 1 : 0) * mp;
    }

    return 0;
  });
}

export function getSubscriptionDiscount(
  subscription: CustomerSubscription,
  renewalPeriod: number = SubscriptionInvoicePeriod.Yearly,
): number {
  return subscription?.renewalDiscounts?.find((discount) => discount.months === renewalPeriod)?.percentOff || 0;
}
